/**
 * Production adapter for the structure set tools — the SAME calls as the
 * structure set menu and settings panel:
 *  - "Create structure set": `useCreateStructureSet()` (null-safe; needs a
 *    patient and a primary image, like the menu item);
 *  - Label / Description / Approval: `useStructureSet()`'s
 *    `setStructureSetLabel`, `setStructureSetDescription` and
 *    `setStructureSetApprovalStatus` (the approval dialog's confirm step,
 *    which records the signed-in user as reviewer). Like the header's
 *    label command, a label / description change arms the unsaved-changes
 *    guard.
 * These hooks swallow errors (console only), so the planner reads every
 * change back. Browser glue covered by the manual demo; the logic is
 * unit-tested through fakes.
 */
import { useCallback } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useSessionOrNull } from '~/contexts/SessionContext';
import type { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import useCreateStructureSet from '~/hooks/structure-set/useCreateStructureSet';
import useStructureSet from '~/hooks/structure-set/useStructureSet';
import { environmentState } from '~/state/atoms/engineAtoms';
import { primaryImageState } from '~/state/atoms/image/imageAtoms';
import { hasUnsavedChangesState } from '~/state/atoms/patientAtoms';
import { useLiveStructureSet } from '../structureManagement';
import type { StructureSetDeps } from './toolRunner';

export function useStructureSetDeps(): () => Omit<
    StructureSetDeps,
    'pendingMutationExists'
> {
    const env = useRecoilValue(environmentState);
    const primaryImage = useRecoilValue(primaryImageState);
    const patient = useSelectedPatient();
    const session = useSessionOrNull();
    const createStructureSet = useCreateStructureSet();
    const {
        setStructureSetLabel,
        setStructureSetDescription,
        setStructureSetApprovalStatus,
    } = useStructureSet();
    const setHasUnsavedChanges = useSetRecoilState(hasUnsavedChangesState);
    const liveSet = useLiveStructureSet();

    return useCallback(
        () => ({
            liveSet,
            currentContextId: () => env?.structureSet?.sopInstanceUID,
            cannotCreateReason: () =>
                !patient || !session
                    ? 'no patient is loaded'
                    : !primaryImage
                      ? 'no image is loaded'
                      : undefined,
            imageName: () =>
                primaryImage
                    ? `the current ${primaryImage.firstSlice.modality} image` +
                      (primaryImage.firstSlice.seriesDescription
                          ? ` (${primaryImage.firstSlice.seriesDescription})`
                          : '')
                    : 'the current image',
            createStructureSet: () => createStructureSet(),
            setLabel: async (label) => {
                await setStructureSetLabel(label);
                setHasUnsavedChanges(true);
            },
            setDescription: async (description) => {
                await setStructureSetDescription(description);
                setHasUnsavedChanges(true);
            },
            setApproval: (status) =>
                setStructureSetApprovalStatus(status as DICOMApprovalStatus),
        }),
        [
            env,
            primaryImage,
            patient,
            session,
            createStructureSet,
            setStructureSetLabel,
            setStructureSetDescription,
            setStructureSetApprovalStatus,
            setHasUnsavedChanges,
            liveSet,
        ]
    );
}
